<template>
  <el-select
    v-model="establishmentModel"
    @change="establishmentChanged"
    autocomplete="new-password"
    :placeholder="$t('ESTABLISHMENT_INTERNAL_ORDERS.PRODUCTION_ESTABLISHMENT')"
    :filterable="filterable"
    :disabled="disabled"
    :loading="loading"
  >
    <el-option v-if="allowNone" :value="null" :label="$t('COMMON.NONE')"> </el-option>
    <el-option
      v-for="establishment in establishments"
      :key="establishment.id"
      :value="establishment.id"
      :label="optionLabel(establishment)"
    >
      <span>{{ establishment.company_name }}</span>
      <el-tag v-if="isInactive(establishment)" size="mini" type="danger" class="ml-2">
        {{ $t("ESTABLISHMENTS.INACTIVE") }}
      </el-tag>
    </el-option>
  </el-select>
</template>

<script>
import { Option, Select, Tag } from "element-ui";
import { fetchProductionEstablishments } from "@/helpers/internalOrderEligibility";

/**
 * Lists only the establishments that may be the production side of an internal
 * order, i.e. those hosting a cafeteria of type PRODUCTION.
 *
 * The rule is resolved client-side from the cafeterias endpoint on purpose: the API
 * carries no check for it.
 *
 * Drop-in for EstablishmentSelector: same `establishmentChanged(id, object)` event.
 */
export default {
  name: "production-establishment-selector",

  components: {
    [Select.name]: Select,
    [Option.name]: Option,
    [Tag.name]: Tag,
  },

  props: {
    establishment: {
      type: String,
      default: null,
      description: "Selected establishment id",
    },
    filterOrganization: {
      type: String,
      default: null,
      description: "Organization id",
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    allowNone: {
      type: Boolean,
      default: false,
    },
    filterable: {
      type: Boolean,
      default: true,
    },
    onlyActive: {
      type: Boolean,
      default: true,
      description: "Hide inactive establishments, except the one already selected",
    },
    restrictToIds: {
      type: Array,
      default: null,
      description: "Only offer these establishment ids (e.g. the user's own), null = all",
    },
  },

  data() {
    return {
      establishmentModel: this.establishment,
      allEstablishments: [],
      loading: false,
    };
  },

  computed: {
    establishments() {
      return this.allEstablishments.filter(
        (establishment) =>
          (!this.restrictToIds || this.restrictToIds.includes(establishment.id)) &&
          (!this.onlyActive ||
            !this.isInactive(establishment) ||
            establishment.id === this.establishmentModel)
      );
    },
  },

  created() {
    this.getEstablishments();
  },

  watch: {
    establishment(value) {
      this.establishmentModel = value || null;
    },
    filterOrganization() {
      this.getEstablishments();
    },
  },

  methods: {
    async getEstablishments() {
      this.loading = true;
      try {
        this.allEstablishments = await fetchProductionEstablishments({
          organizationId: this.filterOrganization,
        });
        this.$emit("loaded", this.allEstablishments);
      } catch (error) {
        this.allEstablishments = [];
        this.$notify({
          type: "danger",
          message: this.$t("ERRORS.SOMETHING_WENT_WRONG"),
        });
      } finally {
        this.loading = false;
      }
    },

    establishmentChanged(establishmentId) {
      const selected = this.allEstablishments.find(
        (item) => item.id === establishmentId
      );
      this.$emit("establishmentChanged", establishmentId, selected);
    },

    isInactive(establishment) {
      return (
        !!establishment &&
        (establishment.active === false || establishment.active === 0)
      );
    },

    optionLabel(establishment) {
      return this.isInactive(establishment)
        ? `${establishment.company_name} (${this.$t("ESTABLISHMENTS.INACTIVE")})`
        : establishment.company_name;
    },
  },
};
</script>
